// FILE: taskCompletion.logic.ts
// Purpose: Detects new thread lifecycle notifications and builds alert copy.
// Layer: Notification logic
// Exports: lifecycle detection helpers and notification copy helpers

import { DELEGATION_WAKE_SKIPPED_ACTIVITY_KIND } from "@taskin-code/contracts";
import { isDelegateThread } from "@taskin-code/shared/delegation";
import {
  isThreadSnoozed,
  threadHasUnseenCompletion,
  threadReturnedFromSnooze,
} from "../lib/threadInboxState";
import {
  defaultTerminalTitleForCliKind,
  type TerminalCliKind,
  type TerminalVisualState,
} from "@taskin-code/shared/terminalThreads";
import type { Thread, ThreadSession } from "../types";
import {
  derivePendingApprovals,
  derivePendingUserInputs,
  hasLiveLatestTurn,
  type DelegationState,
} from "../session-logic";
import { resolveSubagentPresentationForThread } from "../lib/subagentPresentation";

export interface CompletedThreadCandidate {
  threadId: Thread["id"];
  projectId: Thread["projectId"];
  title: string;
  completedAt: string;
  assistantSummary: string | null;
}

export interface SnoozeReturnCandidate {
  threadId: Thread["id"];
  projectId: Thread["projectId"];
  title: string;
  wokenAt: string;
  /**
   * What finished while the thread was put aside, when something did. This is
   * the message whose notification the snooze swallowed, handed back late
   * rather than lost.
   */
  assistantSummary: string | null;
  hasUnseenCompletion: boolean;
}

export interface ThreadAttentionCandidate {
  kind: "approval" | "user-input" | "delegation-stalled";
  threadId: Thread["id"];
  projectId: Thread["projectId"];
  title: string;
  /**
   * How to name a delegation that needs the user, when this candidate is one.
   *
   * A delegation's own title is written by the agent that opened it and means
   * nothing out of context ("subagent:thr_1:toolu_9", "New thread"), so the
   * alert names the subagent and the thread it belongs to instead.
   */
  delegateAttribution?: { subagentLabel: string; parentTitle: string } | undefined;
  requestId: string;
  createdAt: string;
  requestKind?: "command" | "file-read" | "file-change";
  summary?: string;
}

interface TerminalNotificationThreadState {
  runningTerminalIds: string[];
  terminalAttentionStatesById: Record<string, "attention" | "review">;
  terminalCliKindsById: Record<string, TerminalCliKind>;
  terminalIds: string[];
  terminalLabelsById: Record<string, string>;
  terminalTitleOverridesById: Record<string, string>;
}

export interface CompletedTerminalCandidate {
  cliKind: TerminalCliKind | null;
  terminalId: string;
  threadId: Thread["id"];
  title: string;
}

export interface TerminalAttentionCandidate {
  cliKind: TerminalCliKind | null;
  terminalId: string;
  threadId: Thread["id"];
  title: string;
}

type ThreadSessionStatus = ThreadSession["status"];

// Treat sidebar "working" states as the only notification-worthy starting point.
function isRunningStatus(status: ThreadSessionStatus | null | undefined): boolean {
  return status === "running" || status === "connecting";
}

// Build a short body from the latest assistant message without dumping long output into OS chrome.
function summarizeLatestAssistantMessage(thread: Thread): string | null {
  for (let index = thread.messages.length - 1; index >= 0; index -= 1) {
    const message = thread.messages[index];
    if (!message || message.role !== "assistant") {
      continue;
    }
    const trimmed = message.text.trim().replace(/\s+/g, " ");
    if (trimmed.length === 0) {
      continue;
    }
    return trimmed.length <= 140 ? trimmed : `${trimmed.slice(0, 137)}...`;
  }
  return null;
}

function hadUnsettledTurn(thread: Thread | undefined): boolean {
  if (!thread) {
    return false;
  }
  if (hasLiveLatestTurn(thread.latestTurn, thread.session)) {
    return true;
  }
  return !thread.latestTurn?.completedAt && isRunningStatus(thread.session?.status);
}

function isCompletionNotificationSettled(thread: Thread | undefined): boolean {
  if (!thread?.latestTurn?.startedAt || !thread.latestTurn.completedAt) {
    return false;
  }
  if (!thread.session) {
    return true;
  }
  return thread.session.orchestrationStatus !== "running";
}

/**
 * Whether this thread still has delegation work running underneath it.
 *
 * A delegating agent is told to end its turn and be woken when its delegates
 * report, so the end of that turn is a technical pause, not a finish line.
 * Announcing it turns one orchestration into one alert per round, none of which
 * is the news the user is waiting for.
 */
function hasWorkRunningUnderneath(
  thread: Thread,
  delegationStateByParent: ReadonlyMap<Thread["id"], DelegationState> | undefined,
): boolean {
  const delegation = delegationStateByParent?.get(thread.id);
  if (!delegation) {
    return false;
  }
  return delegation.liveCount > 0 || delegation.hasLiveDescendants;
}

// Compare consecutive snapshots and emit fresh settled completions, even if the
// session snapshot skips directly to ready before the toast logic observes it.
export function collectCompletedThreadCandidates(
  previousThreads: readonly Thread[],
  nextThreads: readonly Thread[],
  delegationStateByParent?: ReadonlyMap<Thread["id"], DelegationState>,
): CompletedThreadCandidate[] {
  const previousById = new Map(previousThreads.map((thread) => [thread.id, thread] as const));
  const candidates: CompletedThreadCandidate[] = [];

  for (const thread of nextThreads) {
    const previousThread = previousById.get(thread.id);
    if (!previousThread) {
      continue;
    }

    // A delegation finishing is its parent's business: the wake reactor picks the
    // report up on its own, and the user is not expected to open the child at
    // all. Judged by role rather than by `isLiveDelegateThread` so closing the
    // delegation — which archives it — does not hand it back its notifications.
    if (isDelegateThread(thread)) {
      continue;
    }
    if (hasWorkRunningUnderneath(thread, delegationStateByParent)) {
      continue;
    }

    // Snoozing is a request not to be told about this thread, and a completion
    // is the notification people most want silenced when they set one: "I know
    // it is working, tell me tomorrow". Only completions — the attention
    // notifications (approvals, questions) deliberately still come through,
    // since those are the ones that break the snooze in the sidebar too.
    if (isThreadSnoozed(thread, Date.now())) {
      continue;
    }

    const completedAt = thread.latestTurn?.completedAt;
    if (!completedAt) {
      continue;
    }
    if (!isCompletionNotificationSettled(thread)) {
      continue;
    }
    if (!previousThread.session && !previousThread.latestTurn?.completedAt) {
      continue;
    }
    if (!hadUnsettledTurn(previousThread) && !previousThread.latestTurn?.completedAt) {
      continue;
    }
    if (
      previousThread.latestTurn?.turnId === thread.latestTurn?.turnId &&
      isCompletionNotificationSettled(previousThread)
    ) {
      continue;
    }

    candidates.push({
      threadId: thread.id,
      projectId: thread.projectId,
      title: thread.title,
      completedAt,
      assistantSummary: summarizeLatestAssistantMessage(thread),
    });
  }

  return candidates;
}
function resolveTerminalNotificationState(
  threadState: TerminalNotificationThreadState | undefined,
  terminalId: string,
): TerminalVisualState {
  if (!threadState) {
    return "idle";
  }
  if (threadState.terminalAttentionStatesById?.[terminalId] === "attention") {
    return "attention";
  }
  if ((threadState.runningTerminalIds ?? []).includes(terminalId)) {
    return "running";
  }
  if (threadState.terminalAttentionStatesById?.[terminalId] === "review") {
    return "review";
  }
  return "idle";
}

function resolveTerminalNotificationTitle(
  threadState: TerminalNotificationThreadState | undefined,
  terminalId: string,
): { cliKind: TerminalCliKind | null; title: string } {
  const cliKind = threadState?.terminalCliKindsById?.[terminalId] ?? null;
  const title =
    threadState?.terminalTitleOverridesById?.[terminalId]?.trim() ||
    threadState?.terminalLabelsById?.[terminalId]?.trim() ||
    (cliKind ? defaultTerminalTitleForCliKind(cliKind) : "Terminal");
  return { cliKind, title };
}

export function collectCompletedTerminalCandidates(
  previousByThreadId: Record<string, TerminalNotificationThreadState>,
  nextByThreadId: Record<string, TerminalNotificationThreadState>,
): CompletedTerminalCandidate[] {
  const threadIds = new Set([...Object.keys(previousByThreadId), ...Object.keys(nextByThreadId)]);
  const candidates: CompletedTerminalCandidate[] = [];

  for (const threadId of threadIds) {
    const previousThreadState = previousByThreadId[threadId];
    const nextThreadState = nextByThreadId[threadId];
    const terminalIds = new Set([
      ...(previousThreadState?.terminalIds ?? []),
      ...(nextThreadState?.terminalIds ?? []),
    ]);

    for (const terminalId of terminalIds) {
      const previousState = resolveTerminalNotificationState(previousThreadState, terminalId);
      const nextState = resolveTerminalNotificationState(nextThreadState, terminalId);
      if (nextState !== "review" || previousState === "review") {
        continue;
      }
      const { cliKind, title } = resolveTerminalNotificationTitle(nextThreadState, terminalId);
      candidates.push({
        threadId: threadId as Thread["id"],
        terminalId,
        cliKind,
        title,
      });
    }
  }

  return candidates;
}

/**
 * How recent a stall row has to be to be worth announcing.
 *
 * The row is news when it arrives over the push channel — the server stamps it
 * as it decides — but the same row arrives again whenever a thread is hydrated,
 * and hydration is exactly what happens to a thread the user has never opened
 * this session. Judging it by age tells the two apart without giving up the
 * case that matters: a stall in a thread the user is not looking at, which is
 * the whole reason this notification exists.
 */
const STALLED_DELEGATION_FRESHNESS_MS = 5 * 60 * 1000;

/**
 * Delegations that reported back while the server had already decided it would
 * not resume the thread again on its own.
 *
 * This is the one attention state nothing resolves by itself: the report sits
 * unread and the orchestration is stopped until someone writes in the thread.
 * Until it was surfaced here the only trace was a row in the timeline, which is
 * why threads were found hours later still holding a finished report.
 */
function collectStalledDelegationActivities(
  previousThread: Thread,
  thread: Thread,
  now: number,
): ThreadAttentionCandidate[] {
  const previousIds = new Set(previousThread.activities.map((activity) => activity.id));

  const candidates: ThreadAttentionCandidate[] = [];
  for (const activity of thread.activities) {
    if (activity.kind !== DELEGATION_WAKE_SKIPPED_ACTIVITY_KIND) {
      continue;
    }
    if (previousIds.has(activity.id)) {
      continue;
    }
    const createdAt = Date.parse(activity.createdAt);
    if (Number.isNaN(createdAt) || now - createdAt > STALLED_DELEGATION_FRESHNESS_MS) {
      continue;
    }
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const readyCount = typeof payload?.readyCount === "number" ? payload.readyCount : 1;
    candidates.push({
      kind: "delegation-stalled",
      threadId: thread.id,
      projectId: thread.projectId,
      title: thread.title,
      requestId: activity.id,
      createdAt: activity.createdAt,
      summary:
        readyCount === 1
          ? "A delegation finished and is waiting to be read. Write in the thread to pick it up."
          : `${readyCount} delegations finished and are waiting to be read. Write in the thread to pick them up.`,
    });
  }
  return candidates;
}

/**
 * Names a delegation that needs the user by its subagent label and its parent.
 *
 * Returns nothing for any thread that is not a delegation, and for one whose
 * parent is missing from the snapshot — an orphan is better announced by its own
 * title than by half a sentence.
 */
function resolveDelegateAttribution(
  thread: Thread,
  threads: readonly Thread[],
): ThreadAttentionCandidate["delegateAttribution"] {
  if (!isDelegateThread(thread) || !thread.parentThreadId) {
    return undefined;
  }
  const parent = threads.find((candidate) => candidate.id === thread.parentThreadId);
  if (!parent) {
    return undefined;
  }
  const presentation = resolveSubagentPresentationForThread({ thread, threads });
  return {
    subagentLabel: presentation.primaryLabel,
    parentTitle: parent.title.trim() || "Untitled thread",
  };
}

function approvalSummary(requestKind: "command" | "file-read" | "file-change"): string {
  switch (requestKind) {
    case "command":
      return "Command approval requested.";
    case "file-read":
      return "File-read approval requested.";
    case "file-change":
      return "File-change approval requested.";
  }
}

// Compare consecutive activity snapshots and emit only fresh input-needed transitions.
export function collectThreadAttentionCandidates(
  previousThreads: readonly Thread[],
  nextThreads: readonly Thread[],
  now: number = Date.now(),
): ThreadAttentionCandidate[] {
  const previousById = new Map(previousThreads.map((thread) => [thread.id, thread] as const));
  const candidates: ThreadAttentionCandidate[] = [];

  for (const thread of nextThreads) {
    const previousThread = previousById.get(thread.id);
    if (!previousThread) {
      continue;
    }

    const previousApprovalIds = new Set(
      derivePendingApprovals(previousThread.activities, previousThread.latestTurn ?? null).map(
        (approval) => approval.requestId,
      ),
    );
    const previousUserInputIds = new Set(
      derivePendingUserInputs(previousThread.activities, previousThread.latestTurn ?? null).map(
        (request) => request.requestId,
      ),
    );

    // A delegation that is blocked stays announced, unlike one that merely
    // finished: nobody but the user can answer an approval or a question, and
    // the parent waits forever on a delegate nobody unblocked.
    const delegateAttribution = resolveDelegateAttribution(thread, nextThreads);

    for (const approval of derivePendingApprovals(thread.activities, thread.latestTurn ?? null)) {
      if (previousApprovalIds.has(approval.requestId)) {
        continue;
      }
      candidates.push({
        kind: "approval",
        threadId: thread.id,
        projectId: thread.projectId,
        title: thread.title,
        requestId: approval.requestId,
        createdAt: approval.createdAt,
        requestKind: approval.requestKind,
        delegateAttribution,
      });
    }

    for (const request of derivePendingUserInputs(thread.activities, thread.latestTurn ?? null)) {
      if (previousUserInputIds.has(request.requestId)) {
        continue;
      }
      candidates.push({
        kind: "user-input",
        threadId: thread.id,
        projectId: thread.projectId,
        title: thread.title,
        requestId: request.requestId,
        createdAt: request.createdAt,
        delegateAttribution,
      });
    }

    candidates.push(...collectStalledDelegationActivities(previousThread, thread, now));
  }

  return candidates.toSorted((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export function collectTerminalAttentionCandidates(
  previousByThreadId: Record<string, TerminalNotificationThreadState>,
  nextByThreadId: Record<string, TerminalNotificationThreadState>,
): TerminalAttentionCandidate[] {
  const threadIds = new Set([...Object.keys(previousByThreadId), ...Object.keys(nextByThreadId)]);
  const candidates: TerminalAttentionCandidate[] = [];

  for (const threadId of threadIds) {
    const previousThreadState = previousByThreadId[threadId];
    const nextThreadState = nextByThreadId[threadId];
    const terminalIds = new Set([
      ...(previousThreadState?.terminalIds ?? []),
      ...(nextThreadState?.terminalIds ?? []),
    ]);

    for (const terminalId of terminalIds) {
      const previousState = resolveTerminalNotificationState(previousThreadState, terminalId);
      const nextState = resolveTerminalNotificationState(nextThreadState, terminalId);
      if (nextState !== "attention" || previousState === "attention") {
        continue;
      }
      const { cliKind, title } = resolveTerminalNotificationTitle(nextThreadState, terminalId);
      candidates.push({
        threadId: threadId as Thread["id"],
        terminalId,
        cliKind,
        title,
      });
    }
  }

  return candidates;
}

/**
 * The snoozes that ran out since the last snapshot.
 *
 * This is what makes snoozing a *delay* rather than a cancellation. A turn that
 * finishes while the thread is put aside is deliberately not announced
 * (`collectCompletedThreadCandidates` drops it), and it never would be: by the
 * time the deadline passes the turn is no longer new, so the completion pass
 * skips it a second time on the "same turn, already settled" rule. The user had
 * asked to be told tomorrow and tomorrow never came. This pass is tomorrow.
 *
 * Driven by `wokenAt` rather than by `snoozedUntil` going null, so lifting a
 * snooze by hand — which clears the deadline without stamping `wokenAt` — stays
 * silent. Somebody who just un-snoozed a thread is already looking at it.
 */
export function collectReturnedFromSnoozeCandidates(
  previousThreads: readonly Thread[],
  nextThreads: readonly Thread[],
): SnoozeReturnCandidate[] {
  const previousById = new Map(previousThreads.map((thread) => [thread.id, thread] as const));
  const candidates: SnoozeReturnCandidate[] = [];

  for (const thread of nextThreads) {
    const previousThread = previousById.get(thread.id);
    // Unknown threads are skipped for the same reason completions skip them:
    // the first snapshot after a reconnect would otherwise announce every
    // snooze that expired while the app was closed, all at once.
    if (!previousThread) continue;
    // A delegation is its parent's business, snoozed or not.
    if (isDelegateThread(thread)) continue;
    if (!threadReturnedFromSnooze(thread)) continue;
    // Already announced on an earlier snapshot: the mark stays true until the
    // thread is opened, so the transition is what is news, not the state.
    if (threadReturnedFromSnooze(previousThread)) continue;

    const wokenAt = thread.wokenAt;
    if (!wokenAt) continue;

    const hasUnseen = threadHasUnseenCompletion(thread);
    candidates.push({
      threadId: thread.id,
      projectId: thread.projectId,
      title: thread.title,
      wokenAt,
      assistantSummary: hasUnseen ? summarizeLatestAssistantMessage(thread) : null,
      hasUnseenCompletion: hasUnseen,
    });
  }

  return candidates;
}

// Keep toast and OS notification copy aligned across browser and desktop surfaces.
export function buildTaskCompletionCopy(candidate: CompletedThreadCandidate): {
  title: string;
  body: string;
} {
  const normalizedTitle = candidate.title.trim();
  const threadLabel = normalizedTitle.length > 0 ? normalizedTitle : "Untitled thread";

  return {
    title: threadLabel,
    body: candidate.assistantSummary || "Finished working.",
  };
}

export function buildThreadAttentionCopy(candidate: ThreadAttentionCandidate): {
  title: string;
  body: string;
} {
  const normalizedTitle = candidate.title.trim();
  const threadLabel = candidate.delegateAttribution
    ? `Subagent "${candidate.delegateAttribution.subagentLabel}" in "${candidate.delegateAttribution.parentTitle}"`
    : normalizedTitle.length > 0
      ? normalizedTitle
      : "Untitled thread";
  const summary =
    candidate.summary ??
    (candidate.kind === "approval"
      ? approvalSummary(candidate.requestKind ?? "command")
      : "User input requested.");

  return {
    // A stalled delegation is not a request the agent made — it is work that
    // stopped — so it says so instead of borrowing the approval wording.
    title: candidate.kind === "delegation-stalled" ? "Delegation waiting" : "Input needed",
    body: `${threadLabel}: ${summary}`,
  };
}

export function buildSnoozeReturnCopy(candidate: SnoozeReturnCandidate): {
  title: string;
  body: string;
} {
  const normalizedTitle = candidate.title.trim();
  const threadLabel = normalizedTitle.length > 0 ? normalizedTitle : "Untitled thread";

  return {
    title: threadLabel,
    // Two different pieces of news wear the same alarm: "it finished while you
    // were away" and "you asked me to remind you". Saying which one it is
    // matters more than the words, because only the first has anything to read.
    body: candidate.hasUnseenCompletion
      ? candidate.assistantSummary || "Volvió de pospuesto y ya había terminado."
      : "Volvió de pospuesto.",
  };
}

export function buildTerminalCompletionCopy(candidate: CompletedTerminalCandidate): {
  title: string;
  body: string;
} {
  const terminalLabel = candidate.title.trim() || "Terminal";
  return {
    title: "Terminal task completed",
    body: `${terminalLabel} finished working.`,
  };
}

export function buildTerminalAttentionCopy(candidate: TerminalAttentionCandidate): {
  title: string;
  body: string;
} {
  const terminalLabel = candidate.title.trim() || "Terminal";
  return {
    title: "Terminal input needed",
    body: `${terminalLabel} needs your attention.`,
  };
}

export function shouldSuppressVisibleThreadNotification(input: {
  threadId: Thread["id"];
  visibleThreadIds: ReadonlySet<Thread["id"]>;
  windowForeground: boolean;
}): boolean {
  return input.windowForeground && input.visibleThreadIds.has(input.threadId);
}

export const collectInputNeededThreadCandidates = collectThreadAttentionCandidates;

export const buildInputNeededCopy = buildThreadAttentionCopy;
